import {
    BfClientType,
    BfEventOrigin,
    BfEventSimple,
    BfEventStatus,
    BfEventType,
    BfGroupName,
    BfManagedCreationSource,
    BfManagedRevisionState,
    BfMapLayerGroupId,
    BfMapMarkerType,
    BfMapProjection,
    BfMapSourceType,
    BfMapStyleType,
    BfMediaLicence,
    BfMediaReportReason,
    BfNotificationPreset,
    BfNotificationSound,
    BfPlanTypes,
    BfPublishingStatus,
    BfRegionName,
    BfRoadStatus,
    BfSigninProvider,
    BfSourceProcess,
    DistanceUnits,
    SpeedUnits,
    TemperatureUnits,
} from "./api/models";
import { BfAlertLevel } from "./api/models/bf-alert-level";
import { BfEventClass } from "./api/models/bf-event-class";
import { BfEventClassification } from "./api/models/bf-event-classification";
import { DEBUG_MODE } from "./client";
import { MarkerOutlineVariant } from "./markers";

export const AVAILABLE_EVENT_CLASSES_NOTIFICATIONS: BfEventClass[] = [
    BfEventClass.warning,
    BfEventClass.incident,
    BfEventClass.predict,
];

export const AVAILABLE_EVENT_CLASSES_MANAGEMENT: BfEventClass[] = [
    BfEventClass.warning,
    BfEventClass.incident,
    BfEventClass.advisory,
    BfEventClass.burnarea,
    BfEventClass.predict,
];

export const AVAILABLE_EVENT_ORIGINS: BfEventOrigin[] = [
    BfEventOrigin.agency,
    BfEventOrigin.predicted,
    BfEventOrigin.automated,
];

export const AVAILABLE_ALERT_LEVELS: BfAlertLevel[] = [
    BfAlertLevel.emergencyWarning,
    BfAlertLevel.evacuateImmediately,
    BfAlertLevel.watchAndAct,
    BfAlertLevel.advice,
    BfAlertLevel.communityUpdate,
    BfAlertLevel.notApplicable,
    BfAlertLevel.unknown,
];

export const AVAILABLE_EVENT_STATUS: BfEventStatus[] = [
    BfEventStatus.active,
    BfEventStatus.controlled,
    BfEventStatus.closed,
];

export const AVAILABLE_REGIONS: BfRegionName[] = [
    BfRegionName.global,
    BfRegionName.au,
    BfRegionName.ca,
    BfRegionName.na,
    BfRegionName.usa,
];

// BfMapSourceType
export const AVAILABLE_SOURCE_TYPES: BfMapSourceType[] = [
    BfMapSourceType.builtin,
    BfMapSourceType.remoteGeoJson,
];

// BfMapStyleType
export const AVAILABLE_STYLE_TYPES: BfMapStyleType[] = [
    BfMapStyleType.fill,
    BfMapStyleType.symbol,
    BfMapStyleType.circle,
    BfMapStyleType.line,
    // BfMapStyleType.heatmap,
    // BfMapStyleType.raster,
];

export const AVAILABLE_GROUP_NAME: BfGroupName[] = [
    BfGroupName.user,
    BfGroupName.pro,
    BfGroupName.business,
    BfGroupName.developer,
    BfGroupName.licencing,
    BfGroupName.history,
    BfGroupName.management,
    BfGroupName.email,
    BfGroupName.root,
    BfGroupName.webhook,
    BfGroupName.enterprise,
    // BfGroupName.deprecated,
];

// BfMapProjection
export const AVAILABLE_PROJECTIONS: BfMapProjection[] = [
    BfMapProjection.mercator,
    BfMapProjection.globe,
];

export const AVAILABLE_PRESETS: BfNotificationPreset[] = [
    BfNotificationPreset.warningsAndIncidentsRegion,
    BfNotificationPreset.warningsRegion,
    BfNotificationPreset.incidentsNeighbourhood,
    BfNotificationPreset.predictions,
    BfNotificationPreset.advanced,
];

/// These event classifications are available for management and have necessary map markers configured.
export const AVAILABLE_EVENT_CLASSIFICATIONS: BfEventClassification[] = [
    BfEventClassification.fire,
    BfEventClassification.storm,
    BfEventClassification.damagingWinds,
    BfEventClassification.burn,
    BfEventClassification.flood,
    BfEventClassification.evacuation,
    BfEventClassification.hail,
    BfEventClassification.cycloneTornado,
    BfEventClassification.tornado,
    BfEventClassification.extremeHeat,
    BfEventClassification.other,
    BfEventClassification.earthquake,
    BfEventClassification.hazardousMaterial,
    BfEventClassification.smoke,
    BfEventClassification.tsunami,
    BfEventClassification.weather,
    BfEventClassification.medical,
    BfEventClassification.fireWeather,
    BfEventClassification.unknown,
    BfEventClassification.airQuality,
    BfEventClassification.dangerousAnimal,
    BfEventClassification.utilitySupply,
    BfEventClassification.shark,
    BfEventClassification.vehicleAccident,
    BfEventClassification.rescue,
    BfEventClassification.damage,
    BfEventClassification.marine,
    BfEventClassification.assistOtherAgency,
];

export enum EventClassificationGroup {
    FireAndSmoke = "Fire and Smoke",
    SevereWeather = "Severe Weather",
    FloodingAndMarine = "Flooding and Marine",
    EmergencyResponse = "Emergency Response",
    Infrastructure = "Infrastructure",
    Environmental = "Environmental",
    Other = "Other",
}

const MAP_EVENT_CLASSIFICATION_GROUPS: Record<EventClassificationGroup, string> = {
    [EventClassificationGroup.FireAndSmoke]: "Fire and Smoke",
    [EventClassificationGroup.SevereWeather]: "Severe Weather",
    [EventClassificationGroup.FloodingAndMarine]: "Flooding and Marine",
    [EventClassificationGroup.EmergencyResponse]: "Emergency Response",
    [EventClassificationGroup.Infrastructure]: "Infrastructure",
    [EventClassificationGroup.Environmental]: "Environmental",
    [EventClassificationGroup.Other]: "Other",
};

export const AVAILABLE_EVENT_CLASSIFICATIONS_GROUPED: Record<
    EventClassificationGroup,
    BfEventClassification[]
> = {
    [EventClassificationGroup.FireAndSmoke]: [
        BfEventClassification.fire,
        BfEventClassification.burn,
        BfEventClassification.smoke,
        BfEventClassification.fireWeather,
    ],
    [EventClassificationGroup.SevereWeather]: [
        BfEventClassification.storm,
        BfEventClassification.damagingWinds,
        BfEventClassification.hail,
        BfEventClassification.cycloneTornado,
        BfEventClassification.tornado,
        BfEventClassification.extremeHeat,
        BfEventClassification.weather,
    ],
    [EventClassificationGroup.FloodingAndMarine]: [
        BfEventClassification.flood,
        BfEventClassification.marine,
        BfEventClassification.tsunami,
        BfEventClassification.shark,
    ],
    [EventClassificationGroup.EmergencyResponse]: [
        BfEventClassification.evacuation,
        BfEventClassification.medical,
        BfEventClassification.vehicleAccident,
        BfEventClassification.rescue,
        BfEventClassification.assistOtherAgency,
    ],
    [EventClassificationGroup.Infrastructure]: [
        BfEventClassification.utilitySupply,
        BfEventClassification.damage,
        BfEventClassification.hazardousMaterial,
    ],
    [EventClassificationGroup.Environmental]: [
        BfEventClassification.dangerousAnimal,
        BfEventClassification.earthquake,
        BfEventClassification.airQuality,
    ],
    [EventClassificationGroup.Other]: [BfEventClassification.unknown, BfEventClassification.other],
};

export const AVAILABLE_EVENT_CLASSIFICATIONS_NOTIFICATIONS: BfEventClassification[] = [
    BfEventClassification.fire,
    BfEventClassification.flood,
    BfEventClassification.evacuation,
    BfEventClassification.cycloneTornado,
    BfEventClassification.tornado,
    BfEventClassification.extremeHeat,
    BfEventClassification.storm,
    BfEventClassification.hail,
    BfEventClassification.fireWeather,
    BfEventClassification.damagingWinds,
    BfEventClassification.earthquake,
    BfEventClassification.tsunami,
    BfEventClassification.hazardousMaterial,
    BfEventClassification.weather,
    BfEventClassification.smoke,
    BfEventClassification.medical,
    BfEventClassification.burn,
    BfEventClassification.rescue,
    BfEventClassification.damage,
    BfEventClassification.marine,
    BfEventClassification.airQuality,
    BfEventClassification.dangerousAnimal,
    BfEventClassification.shark,
    BfEventClassification.vehicleAccident,
    BfEventClassification.assistOtherAgency,
    BfEventClassification.unknown,
    BfEventClassification.other,
];

export const AVAILABLE_DISTANCE_UNITS: DistanceUnits[] = [
    DistanceUnits.kilometers,
    DistanceUnits.miles,
];

export const MAP_LAYER_GROUP_ID: Record<BfMapLayerGroupId, string> = {
    NotUserSelectable: "",
    Warnings: "Warnings",
    Incidents: "Incidents",
    PlannedBurns: "Planned Burns",
    BurnAreas: "Burn Areas",
    Velocity: "",
    Aircraft: "Aircraft",
    HazardousRoads: "Hazardous Roads",
    ClosedRoads: "Closed Roads",
    Hotspots: "Hotspots",
    Notifications: "",
    Weather: "Weather",
    Lightning: "",
    WeatherWarnings: "Weather Warnings",
    CustomLayers: "Custom Layer",
    GeoSatellite: "",
    Basemap: "",
    Radar: "",
    FireDanger: "Fire Danger",
    Ignitions: "Potential Ignition",
    Media: "Media",
};

const MAP_DISTANCE_UNITS: Record<DistanceUnits, string> = {
    kilometers: "km",
    miles: "mi",
    nauticalMiles: "Nautical Miles",
    feet: "ft",
    meters: "m",
    millimeters: "mm",
    centimeters: "cm",
    inches: "in",
};

export const AVAILABLE_SPEED_UNITS: SpeedUnits[] = [
    SpeedUnits.kmph,
    SpeedUnits.knots,
    SpeedUnits.mph,
];

const MAP_SPEED_UNITS: Record<SpeedUnits, string> = {
    kmph: "km/h",
    knots: "kts",
    mph: "mph",
};

export const AVAILABLE_TEMPERATURE_UNITS: TemperatureUnits[] = [
    TemperatureUnits.celsius,
    TemperatureUnits.fahrenheit,
];

const MAP_TEMPERATURE_UNITS: Record<TemperatureUnits, string> = {
    celsius: "°C",
    fahrenheit: "°F",
};

export const AVAILABLE_RAIN_RATE_UNITS: DistanceUnits[] = [
    DistanceUnits.millimeters,
    DistanceUnits.inches,
];

const MAP_EVENT_CLASSES: Record<BfEventClass, BfMapMarkerType> = {
    unknown: BfMapMarkerType.warningGenericWhite,
    warning: BfMapMarkerType.warningGenericWhite,
    incident: BfMapMarkerType.incidentFireControlled,
    burnarea: BfMapMarkerType.burnArea,
    advisory: BfMapMarkerType.advisoryOther,
    predict: BfMapMarkerType.incidentFireUncontrolled,
};

const MAP_EVENT_CLASS_OUTLINES: Partial<Record<BfEventClass, MarkerOutlineVariant>> = {
    predict: "predicted",
};

const MAP_EVENT_STATUS: Record<BfEventStatus, BfMapMarkerType> = {
    active: BfMapMarkerType.incidentFireUncontrolled,
    controlled: BfMapMarkerType.incidentFireControlled,
    closed: BfMapMarkerType.incidentOther,
    escalated: BfMapMarkerType.incidentFireUncontrolled,
    deescalated: BfMapMarkerType.incidentFireControlled,
};

const MAP_ALERT_LEVEL: Record<BfAlertLevel, BfMapMarkerType> = {
    advice: BfMapMarkerType.warningFireAdvice,
    communityUpdate: BfMapMarkerType.warningInformation,
    emergencyWarning: BfMapMarkerType.warningFireEmergencyWarning,
    evacuateImmediately: BfMapMarkerType.warningFireEvacuateNow,
    notApplicable: BfMapMarkerType.warningNotApplicable,
    unknown: BfMapMarkerType.warningUnknown,
    watchAndAct: BfMapMarkerType.warningFireWatchAndAct,
};

const MAP_CLIENT_TYPE: Record<BfClientType, string> = {
    web: "Web",
    ios: "App",
    pwatwa: "PWA/TWA",
    "ios,signinwithapple": "App",
    "ios,SfSafariViewController": "App",
    android: "App",
};

const MAP_ICON_EVENT_CLASSIFICATION: Record<BfEventClassification, BfMapMarkerType> = {
    unknown: BfMapMarkerType.incidentOther,
    bushfire: BfMapMarkerType.incidentFireControlled,
    fire: BfMapMarkerType.incidentFireControlled,
    storm: BfMapMarkerType.incidentStorm,
    damagingWinds: BfMapMarkerType.incidentWindDamaging,
    burn: BfMapMarkerType.incidentFireBurn,
    flood: BfMapMarkerType.incidentFlood,
    aircraftAccident: BfMapMarkerType.incidentSearchRescue,
    railAccident: BfMapMarkerType.incidentSearchRescue,
    marineAccident: BfMapMarkerType.incidentSearchRescue,
    earthquake: BfMapMarkerType.incidentEarthquake,
    tsunami: BfMapMarkerType.incidentTsunami,
    landslide: BfMapMarkerType.incidentSearchRescue,
    cycloneTornado: BfMapMarkerType.incidentCycloneTornado,
    hazardousMaterial: BfMapMarkerType.incidentHazmat,
    fallenPowerLines: BfMapMarkerType.incidentPowerlines,
    plague: BfMapMarkerType.incidentHealth,
    damFailure: BfMapMarkerType.incidentFlood,
    healthHazard: BfMapMarkerType.incidentHealth,
    alarm: BfMapMarkerType.incidentOther,
    vehicleAccident: BfMapMarkerType.incidentVehicleAccident,
    rescue: BfMapMarkerType.incidentSearchRescue,
    damage: BfMapMarkerType.incidentTreeDown,
    treeDown: BfMapMarkerType.incidentTreeDown,
    fireBan: BfMapMarkerType.incidentOther,
    other: BfMapMarkerType.incidentOther,
    ambulance: BfMapMarkerType.incidentHealth,
    weather: BfMapMarkerType.incidentWeather,
    evacuation: BfMapMarkerType.warningEvacuationWhite,
    marine: BfMapMarkerType.incidentWindDamaging,
    farming: BfMapMarkerType.incidentOther,
    airQuality: BfMapMarkerType.incidentAirQuality,
    smoke: BfMapMarkerType.incidentSmoke,
    hail: BfMapMarkerType.advisoryHail,
    damagingSurf: BfMapMarkerType.incidentOther,
    abnormalHighTide: BfMapMarkerType.incidentOther,
    extremeHeat: BfMapMarkerType.warningHeatWhite,
    dangerousAnimal: BfMapMarkerType.incidentDangerousAnimal,
    pandemic: BfMapMarkerType.incidentHealth,
    utilitySupply: BfMapMarkerType.incidentPowerlines,
    assistOtherAgency: BfMapMarkerType.incidentAssistOtherAgency,
    shark: BfMapMarkerType.incidentShark,
    medical: BfMapMarkerType.incidentHealth,
    schoolClosure: BfMapMarkerType.incidentOther,
    fireWeather: BfMapMarkerType.advisoryFireWeather,
    tornado: BfMapMarkerType.incidentCycloneTornado,
};

const ALERT_LEVEL_FRIENDLY: Record<BfAlertLevel, string> = {
    unknown: "Unknown",
    notApplicable: "Not Applicable",
    communityUpdate: "Community Update",
    advice: "Advice",
    watchAndAct: "Watch and Act",
    emergencyWarning: "Emergency Warning",
    evacuateImmediately: "Evacuate Now",
};

const EVENT_CLASSIFICATION_FRIENDLY: Record<BfEventClassification, string> = {
    bushfire: "Bushfire",
    fire: "Fire",
    unknown: "Unknown",
    storm: "Storm",
    damagingWinds: "Damaging Winds",
    cycloneTornado: "Cyclone / Hurricane",
    tornado: "Tornado",
    burn: "Burn",
    flood: "Flood",
    aircraftAccident: "Aircraft Accident",
    railAccident: "Rail Accident",
    marineAccident: "Marine Accident",
    earthquake: "Earthquake",
    tsunami: "Tsunami",
    landslide: "Landslide",
    hazardousMaterial: "Hazardous Material",
    fallenPowerLines: "Fallen Power Lines",
    plague: "Plague",
    damFailure: "Dam Failure",
    healthHazard: "Health Hazard",
    alarm: "Alarm",
    vehicleAccident: "Vehicle Accident",
    rescue: "Rescue",
    damage: "Damage",
    treeDown: "Tree Down",
    fireBan: "Fire Ban",
    other: "Other",
    ambulance: "Ambulance",
    weather: "Weather",
    evacuation: "Evacuation",
    marine: "Marine",
    farming: "Farming",
    airQuality: "Air Quality",
    smoke: "Smoke",
    hail: "Hail",
    damagingSurf: "Damaging Surf",
    abnormalHighTide: "Abnormal High Tide",
    extremeHeat: "Extreme Heat",
    dangerousAnimal: "Dangerous Animal",
    pandemic: "Pandemic",
    utilitySupply: "Utility Supply",
    assistOtherAgency: "Assist Other Agency",
    shark: "Shark",
    medical: "Medical",
    schoolClosure: "School Closure",
    fireWeather: "Fire Weather",
};

const EVENT_CLASS_FRIENDLY: Record<BfEventClass, string> = {
    unknown: "Unknown",
    warning: "Warnings",
    incident: "Incidents",
    advisory: "Weather Advisories",
    burnarea: "Burn Areas",
    predict: "Possible Ignitions",
};
const EVENT_CLASS_FRIENDLY_DESC: Record<BfEventClass, string> = {
    unknown: "Not better classified",
    warning: "More impactful events",
    incident: "Smaller scale events",
    advisory: "Weather and other advisories",
    burnarea: "Burn Areas",
    predict: "Possible lightning ignitions",
};

const EVENT_ORIGIN_FRIENDLY: Record<BfEventOrigin, string> = {
    agency: "Agency Issued",
    automated: "Satellite Detected",
    predicted: "Possible Ignition (Pro)",
};
const EVENT_ORIGIN_FRIENDLY_DESC: Record<BfEventOrigin, string> = {
    agency: "Issued by an official agency",
    automated: "Detected from satellite observations",
    predicted: "Inferred from lightning and hotspot correlation",
};
const MAP_EVENT_ORIGINS: Record<BfEventOrigin, BfMapMarkerType> = {
    agency: BfMapMarkerType.warningGenericWhite,
    automated: BfMapMarkerType.incidentFireUncontrolled,
    predicted: BfMapMarkerType.incidentFireUncontrolled,
};

const PROCESSOR_FRIENDLY: Record<BfSourceProcess, string> = {
    AnyIncidentSync: "Any",
    VicIncidentSync: "Vic",
    NswIncidentSync: "NSW",
    ActIncidentSync: "ACT",
    QldIncidentSync: "Qld",
    WaIncidentSync: "WA",
    SaIncidentSync: "SA",
    NtIncidentSync: "NT",
    TasIncidentSync: "Tas",
    UsaIncidentSync: "USA",
    RoadsSync: "Roads",
    HotspotsSync: "Hotspots",
    UsaHotspotsSync: "USA",
    WeatherSync: "Weather",
    WindSync: "Wind",
    AdsbxSync: "ADSBx",
    LightningAsync: "Lightning",
    BomSync: "BOM",
    Testy: "Testy",
    Undefined: "Undefined",
    GfsHighResForecastModel: "GFS",
    NamForecastModel: "NAM",
    HrrrForecastModel: "HRRR",
    UsaWeatherSync: "USA Weather",
    CanadaIncidentSync: "Canada Incidents",
    CanadaWeatherObsSync: "Canada Weather Observations",
    UsaWeatherObsSync: "USA Weather Observations",
    AdfdForecastModel: "ADFD",
    AuFireDanger: "Fire Danger",
    Ignition: "Ignitions",
    EuHotspots: "EU Hotspots",
    AsHotspots: "AS Hotspots",
    AfHotspots: "AF Hotspots",
    SaHotspots: "SA Hotspots",
    EuIncidents: "EU Incidents",
    EcmwfIfsOper: "ECMWF IFS Operational",
    EcmwfAifsSingle: "ECMWF AIFS Single",
    EcmwfAifsEns: "ECMWF AIFS Ensemble",
    InIncidents: "India Incidents",
    Media: "Media Ingestion",
    AuEarthquakes: "AU Earthquakes",
    Crawley: "Crawley",
    HotspotDetection: "Hotspot Detection",
    Sentinel3Hotspots: "Sentinel-3 Hotspots",
};

const PROCESSOR_FRIENDLY_LONG: Record<BfSourceProcess, string> = {
    AnyIncidentSync: "Any",
    VicIncidentSync: "Victoria",
    NswIncidentSync: "New South Wales",
    ActIncidentSync: "Australian Capital Territory",
    QldIncidentSync: "Queenslane",
    WaIncidentSync: "Western Australia",
    SaIncidentSync: "South Australia",
    NtIncidentSync: "Northern Territory",
    TasIncidentSync: "Tasmania",
    UsaIncidentSync: "USA",
    RoadsSync: "Roads",
    HotspotsSync: "Hotspots",
    UsaHotspotsSync: "USA",
    WeatherSync: "Weather",
    WindSync: "Wind",
    AdsbxSync: "ADSBx",
    LightningAsync: "Lightning",
    BomSync: "Bureau of Meteorology",
    Testy: "Testy",
    Undefined: "Undefined",
    GfsHighResForecastModel: "GFS",
    NamForecastModel: "NAM",
    HrrrForecastModel: "HRRR",
    UsaWeatherSync: "USA Weather Sync",
    CanadaIncidentSync: "Canada Incident Sync",
    CanadaWeatherObsSync: "Canada Weather Observations",
    UsaWeatherObsSync: "USA Weather Observations",
    AdfdForecastModel: "ADFD",
    AuFireDanger: "Fire Danger",
    Ignition: "Fire Ignitions",
    EuHotspots: "Europe Hotspots",
    AsHotspots: "Asia Hotspots",
    AfHotspots: "Africa Hotspots",
    SaHotspots: "South America Hotspots",
    EuIncidents: "Europe Incidents",
    EcmwfIfsOper: "ECMWF IFS Operational",
    EcmwfAifsSingle: "ECMWF AIFS Single",
    EcmwfAifsEns: "ECMWF AIFS Ensemble",
    InIncidents: "India Incidents",
    Media: "Media Ingestion",
    AuEarthquakes: "Australia Earthquakes",
    Crawley: "Crawley",
    HotspotDetection: "Hotspot Detection",
    Sentinel3Hotspots: "Sentinel-3 Hotspots",
};

const REVISION_FRIENDLY: Record<BfManagedRevisionState, string> = {
    draft: "Draft",
    active: "Active",
    removed: "Removed",
};

const EVENT_STATUS_FRIENDLY: Record<BfEventStatus, string> = {
    active: "Active",
    controlled: "Controlled",
    closed: "Closed",
    escalated: "Escalated",
    deescalated: "Descalated",
};

const CREATION_SOURCE_FRIENDLY: Record<BfManagedCreationSource, string> = {
    manual: "Manual",
    copy: "Copy",
    masked: "Masked",
};

const GROUP_NAME_FRIENDLY: Record<BfGroupName, string> = {
    user: "User",
    pro: "Pro",
    root: "Root",
    developer: "Developer",
    licencing: "Licencing",
    history: "History",
    management: "Management",
    email: "Email",
    business: "Business",
    webhook: "Webhook",
    cloud: "Cloud",
    deprecated: "Deprecated",
    enterprise: "Enterprise",
    moderator: "Moderator",
};

const SOURCE_TYPE_MAP: Record<BfMapSourceType, string> = {
    builtin: "Built-in",
    remoteGeoJson: "Remote GeoJson",
    vector: "Vector Tiles",
    raster: "Raster Tiles",
    websocket: "Websocket Stream",
};

const STYLE_TYPE_MAP: Record<BfMapStyleType, string> = {
    symbol: "Symbol",
    fill: "Fill",
    circle: "Circle",
    line: "Line",
    heatmap: "Heatmap",
    raster: "Raster",
};

const PROJECTIONS: Record<BfMapProjection, string> = {
    mercator: "🌐 Flat Earther (Mercator; Default)",
    globe: "🌏 Earth (Beta)",
};

const PLAN_TYPES: Record<BfPlanTypes, string> = {
    free: "Always Free",
    pro: "Pro",
    business: "Business",
    enterprise: "Enterprise",
    business2Users: "Business (2 Users)",
    business3Users: "Business (3 Users)",
    business5Users: "Business (5 Users)",
    business10Users: "Business (10 Users)",
};

const PLAN_TO_GROUP_TYPES: Record<BfPlanTypes, BfGroupName> = {
    free: BfGroupName.user,
    pro: BfGroupName.pro,
    business: BfGroupName.business,
    enterprise: BfGroupName.enterprise,
    business2Users: BfGroupName.business,
    business3Users: BfGroupName.business,
    business5Users: BfGroupName.business,
    business10Users: BfGroupName.business,
};

const REGIONS_FRIENDLY: Record<BfRegionName, string> = {
    global: "Worldwide",
    au: "Australia",
    na: "North America",
    testy: "Test",
    usa: "USA",
    ca: "Canada",
    as: "Asia",
    sa: "South America",
    eu: "Europe",
    af: "Africa",
    an: "Antarctica",
};

const FRIENDLY_EVENT_TYPE: Record<BfEventType, string> = {
    BfBaseEvent: "Base Event",
    BfEvent: "Event",
    BfRoad: "Road Impact",
    BfFlight: "Aircraft",
    BfHotspot: "Hotspot",
    BfWeatherObservation: "Weather Observation",
    BfLightning: "Lightning Strike",
    BfCustomData: "Custom Data",
    BfRadarGate: "Radar Scan Gate",
    BfVelocity: "Wind Velocity",
    BfFireDanger: "Fire Danger",
    BfIgnition: "Fire Ignition",
    BfMedia: "Media",
};

const FRIENDLY_ROAD_STATUS: Record<BfRoadStatus, string> = {
    closed: "Road Closed",
    impacted: "Road Impacted",
    lanesclosed: "Lanes Closed",
    ok: "Road Open",
};

const FRIENDLY_PROVIDERS: Record<BfSigninProvider, string> = {
    email: "Email",
    microsoft: "Microsoft",
    google: "Google",
    apple: "Apple",
    facebook: "Facebook",
};

const PRESET_MAP: Record<BfNotificationPreset, string> = {
    [BfNotificationPreset.advanced]: "I will configure my own (Advanced)",
    [BfNotificationPreset.warningsRegion]: "Only warnings in my region",
    [BfNotificationPreset.incidentsNeighbourhood]: "Only Incidents in my neighbourhood",
    [BfNotificationPreset.warningsAndIncidentsRegion]: "Both warnings and incidents in my region",
    [BfNotificationPreset.predictions]: "Possible events in your region (Pro)",
};

export const PLAN_NAMES: Record<BfPlanTypes, string> = {
    pro: "Pro",
    business: "Business",
    enterprise: "Enterprise",
    free: "Always Free",
    business2Users: "Business (2 Users)",
    business3Users: "Business (3 Users)",
    business5Users: "Business (5 Users)",
    business10Users: "Business (10 Users)",
};

export const PLAN_RANK: BfPlanTypes[] = [
    "free",
    "pro",
    "business",
    // "business2Users",
    // "business3Users",
    // "business5Users",
    // "business10Users",
    "enterprise",
];

export enum KNOWN_ERRORS {
    FB_LOGIN_ERROR_UNABLE_TO_UNPACK = "FB_LOGIN_ERROR_UNABLE_TO_UNPACK",
    FB_LOGIN_ERROR = "FB_LOGIN_ERROR_UNABLE_TO_VALIDATE_TOKEN_OR_EMAIL_NOT_VALIDATED_WITH_FB",
    AP_LOGIN_ERROR = "AP_LOGIN_ERROR_NO_EMAIL_OR_NAME",
    // Returned by the API when an OAuth flow now requires the email OTC step
    // but the client is too old to render it. The new ErrorView arm tells the
    // user to update their app.
    LOGIN_ERROR_UPDATE_REQUIRED = "LOGIN_ERROR_UPDATE_REQUIRED",
    // Returned by /v1/auth/otc when the submitted code doesn't match, has
    // expired, or has already been used.
    OTC_INVALID_OR_EXPIRED = "OTC_INVALID_OR_EXPIRED",
    // Returned by /v1/auth/otc on a new-user-signup validation when the
    // email or user_id was claimed by someone else between OTC issue and
    // validate.
    OTC_USER_ALREADY_EXISTS = "OTC_USER_ALREADY_EXISTS",
    // Returned by /v1/auth/otc when a root user attempts to validate; root
    // accounts can't login via OTC, by design.
    OTC_ROOT_USER_NOT_PERMITTED = "OTC_ROOT_USER_NOT_PERMITTED",

    // Returned by the API when the state parameter in the OAuth flow doesn't match what was issued.
    // This almost always means the user has multiple tabs open and is
    // trying to sign in from more than one of them, but could also be caused by
    // overly aggressive adblockers or similar extensions.
    LOGIN_ERROR_INVALID_STATE = "LOGIN_ERROR_INVALID_STATE",
}

/// Short, user-visible explanation for a known OTC-validation error code.
/// Falls back to the raw code so even unmapped codes are at least readable.
export const otcErrorMessage = (code: string): string => {
    switch (code) {
        case KNOWN_ERRORS.OTC_INVALID_OR_EXPIRED:
            return "That code didn't match, or it has expired. Please check your email and try again — a new code can be requested by restarting the sign-in.";
        case KNOWN_ERRORS.OTC_USER_ALREADY_EXISTS:
            return "An account with this email already exists. Please sign in with that account instead.";
        case KNOWN_ERRORS.OTC_ROOT_USER_NOT_PERMITTED:
            return "This account can't sign in with a code. Please use a different sign-in method.";
        default:
            return `Sign-in code couldn't be validated (${code}).`;
    }
};

export interface RateLimit {
    retryAfterSecs: number | null;
}

type HttpErrorLike = {
    response?: { status?: number; data?: unknown; headers?: Record<string, unknown> };
};

const parseSecs = (value: unknown): number | null => {
    const secs = typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(secs) && secs > 0 ? secs : null;
};

/// The rate limit behind `error` when it is an HTTP 429 from the API or the edge, otherwise null.
export const asRateLimit = (error: unknown): RateLimit | null => {
    const response = (error as HttpErrorLike | null)?.response;
    if (response?.status !== 429) {
        return null;
    }

    const data = response.data as { retry_after_secs?: unknown } | undefined;
    return {
        retryAfterSecs:
            parseSecs(data?.retry_after_secs) ?? parseSecs(response.headers?.["retry-after"]),
    };
};

/// "in 12 minutes", "in a minute" or "in a few minutes" when the wait is unknown.
export const retryAfterText = ({ retryAfterSecs }: RateLimit): string => {
    if (retryAfterSecs === null) {
        return "in a few minutes";
    }
    const minutes = Math.ceil(retryAfterSecs / 60);
    if (minutes <= 1) {
        return "in a minute";
    }
    if (minutes < 120) {
        return `in ${minutes} minutes`;
    }
    return `in ${Math.ceil(minutes / 60)} hours`;
};

/// Shown when another sign-in code is requested too soon.
export const codeRateLimitMessage = (limit: RateLimit): string =>
    `We've already sent you a code. Check your inbox and spam folder, or try again ${retryAfterText(limit)}.`;

export const AVAILABLE_SOUNDS: BfNotificationSound[] = ["default", "cfaSiren"];

export const FRIENDLY_SOUNDS: Record<BfNotificationSound, string> = {
    default: "Default",
    cfaSiren: "CFA Siren",
};

export const ANDROID_SOUNDS: Record<BfNotificationSound, string> = {
    default: "default",
    cfaSiren: "cfa_siren_1.wav",
};

export const PUBLISHING_STATES: Record<BfPublishingStatus, string> = {
    [BfPublishingStatus.waitingManualReview]: "Waiting for Review",
    [BfPublishingStatus.active]: "Approved",
    [BfPublishingStatus.denied]: "Denied",
    [BfPublishingStatus.flagged]: "Flagged",
    [BfPublishingStatus.revoked]: "Revoked",
};

export const PUBLISHING_STATES_EMOJI: Record<BfPublishingStatus, string> = {
    [BfPublishingStatus.waitingManualReview]: "⏰ Waiting for Review",
    [BfPublishingStatus.active]: "🚀 Approved",
    [BfPublishingStatus.denied]: "💀 Denied",
    [BfPublishingStatus.flagged]: "⛳ Flagged",
    [BfPublishingStatus.revoked]: "💣 Revoked",
};

export const MEDIA_REPORT_REASON: Record<BfMediaReportReason, string> = {
    other: "❓Other",
    nsfw: "🔞 Not Safe For Work",
    spam: " 📨 Spam or Misleading",
    irrelevant: " 🗑️ Irrelevant, not related to our mission",
    copyright: " 📄 Copyright Infringement",
    offline: "🛜 Offline / Broken Link",
    position: "📍 Position or Location Issues",
};

export const MODERATION_QUEUE_STATES: BfPublishingStatus[] = [
    BfPublishingStatus.waitingManualReview,
    BfPublishingStatus.denied,
    BfPublishingStatus.flagged,
    BfPublishingStatus.revoked,
    BfPublishingStatus.active,
];

export const MEDIA_LICENCES: Record<BfMediaLicence, string> = {
    dsStandard: "Disaster Science Standard Licence",
    ccBySa40: "Creative Commons Attribution-ShareAlike 4.0 International (CC BY-SA 4.0)",
    ccByNcSa40:
        "Creative Commons Attribution-NonCommercial-ShareAlike 4.0 International (CC BY-NC-SA 4.0)",
    cc0: "Creative Commons Zero (CC0)",
    ccBy40: "Creative Commons Attribution 4.0 International (CC BY 4.0)",
};

export class Friendly {
    static EventClass(event_class: BfEventClass | string): string {
        return EVENT_CLASS_FRIENDLY[event_class as keyof typeof BfEventClass];
    }

    static EventClassDescription(event_class: BfEventClass | string): string {
        return EVENT_CLASS_FRIENDLY_DESC[event_class as keyof typeof BfEventClass];
    }

    static EventClassification(event_classification: BfEventClassification): string {
        return EVENT_CLASSIFICATION_FRIENDLY[event_classification];
    }

    static EventOrigin(origin: BfEventOrigin): string {
        return EVENT_ORIGIN_FRIENDLY[origin];
    }

    static EventOriginDescription(origin: BfEventOrigin): string {
        return EVENT_ORIGIN_FRIENDLY_DESC[origin];
    }

    static AlertLevel(alert_level: BfAlertLevel | string): string {
        return ALERT_LEVEL_FRIENDLY[alert_level as keyof typeof BfAlertLevel];
    }

    static Revision(revision: BfManagedRevisionState): string {
        return REVISION_FRIENDLY[revision];
    }

    static ProcessorLong(processor: BfSourceProcess): string {
        return PROCESSOR_FRIENDLY_LONG[processor];
    }

    static Processor(processor: BfSourceProcess): string {
        return PROCESSOR_FRIENDLY[processor];
    }

    static EventStatus(status: BfEventStatus): string {
        return EVENT_STATUS_FRIENDLY[status];
    }

    static CreationSource(source: BfManagedCreationSource): string {
        return CREATION_SOURCE_FRIENDLY[source];
    }

    static GroupName(group: BfGroupName): string {
        return GROUP_NAME_FRIENDLY[group];
    }

    static Region(region: BfRegionName): string {
        return REGIONS_FRIENDLY[region];
    }

    static SourceType(value: BfMapSourceType): string {
        return SOURCE_TYPE_MAP[value];
    }

    static StyleType(value: BfMapStyleType): string {
        return STYLE_TYPE_MAP[value];
    }

    static Projection(value: BfMapProjection): string {
        return PROJECTIONS[value];
    }

    static Preset(value: BfNotificationPreset): string {
        return PRESET_MAP[value];
    }

    static PlanType(value: BfPlanTypes): string {
        return PLAN_TYPES[value];
    }

    static KnownError(possibleError: string): KNOWN_ERRORS | null {
        const knownError = Object.values(KNOWN_ERRORS).find((value) => {
            const v = value.toString();
            if (possibleError.includes(v)) {
                return value;
            }
        });
        if (!knownError) return null;
        return knownError;
    }

    static IconForEventClass(eventClass: BfEventClass | string): BfMapMarkerType {
        return MAP_EVENT_CLASSES[eventClass as BfEventClass];
    }

    static OutlineForEventClass(
        eventClass: BfEventClass | string
    ): MarkerOutlineVariant | undefined {
        return MAP_EVENT_CLASS_OUTLINES[eventClass as BfEventClass];
    }

    static IconForEventOrigin(origin: BfEventOrigin): BfMapMarkerType {
        return MAP_EVENT_ORIGINS[origin];
    }

    static IconForAlertLevel(alertLevel: BfAlertLevel | string): BfMapMarkerType {
        return MAP_ALERT_LEVEL[alertLevel as BfAlertLevel];
    }

    static IconForEventClassification(eventClassification: BfEventClassification): BfMapMarkerType {
        return MAP_ICON_EVENT_CLASSIFICATION[eventClassification];
    }

    static IconForEventStatus(eventStatus: BfEventStatus): BfMapMarkerType {
        return MAP_EVENT_STATUS[eventStatus];
    }

    static EventType(eventType: BfEventType): string {
        return FRIENDLY_EVENT_TYPE[eventType];
    }

    static RoadStatus(roadStatus: BfRoadStatus): string {
        return FRIENDLY_ROAD_STATUS[roadStatus];
    }

    static LayerGroupId(layerGroupId: BfMapLayerGroupId): string {
        return MAP_LAYER_GROUP_ID[layerGroupId];
    }

    static ClientType(clientType: BfClientType | null | undefined): string {
        if (!clientType) return "Unknown";
        return MAP_CLIENT_TYPE[clientType];
    }

    static SignInProvider(provider: BfSigninProvider): string {
        return FRIENDLY_PROVIDERS[provider];
    }

    /// Attempt to simplistically map an event classification to a BfMapMarkerType, and fetch an icon.
    /// This is non-determinstic compared to how the API handles mapping warnings/incidents, warning levels and event classifications
    /// to icon types.
    static AnyIconForClassification(
        classification: BfEventClassification,
        eventClass?: BfEventClass,
        alertLevel?: BfAlertLevel
    ): BfMapMarkerType | undefined {
        let classificationString = Friendly.titlecase(classification);

        // Fix edge cases, where icons don't match event classification terminology
        classificationString = classificationString
            .replace("HazardousMaterial", "Hazmat")
            .replace("Burn", "FireBurn")
            .replace("ExtremeHeat", "Heat")
            .replace("Medical", "Health")
            .replace("Rescue", "SearchRescue")
            .replace("UtilitySupply", "Powerlines")
            .replace("CycloneTornado", "Cyclone"); // Force to use warning type first, rather than advice;

        // If we have an event class and alert level, we can try to find a specific icon
        console.log("AnyIconForClassification", eventClass, alertLevel);
        if (eventClass && alertLevel) {
            let alertLevelStr = "Advice";

            switch (alertLevel) {
                case BfAlertLevel.emergencyWarning:
                    alertLevelStr = "EmergencyWarning";
                    break;
                case BfAlertLevel.evacuateImmediately:
                    alertLevelStr = "EvacuateImmediately";
                    break;
                case BfAlertLevel.watchAndAct:
                    alertLevelStr = "WatchAndAct";
                    break;
                case BfAlertLevel.communityUpdate:
                    alertLevelStr = "CommunityUpdate";
                    break;
                case BfAlertLevel.notApplicable:
                    alertLevelStr = "NotApplicable";
                    break;
                case BfAlertLevel.unknown:
                    alertLevelStr = "";
                    break;
            }

            const possibleMarker = `${eventClass}${classificationString}${alertLevelStr}`;
            if (possibleMarker in BfMapMarkerType) {
                return possibleMarker as BfMapMarkerType;
            } else {
                if (eventClass === BfEventClass.warning) {
                    return BfMapMarkerType.warningUnknown;
                } else {
                    return BfMapMarkerType.incidentOther;
                }
            }
        } else if (eventClass) {
            const possibleMarker = `${eventClass}${classificationString}`;
            if (possibleMarker in BfMapMarkerType) {
                return possibleMarker as BfMapMarkerType;
            } else {
                return BfMapMarkerType.incidentOther;
            }
        }

        const advisory = `advisory${classificationString}`;
        const incident = `incident${classificationString}`;
        const warning = `warning${classificationString}Advice`;
        const warningNoSuffix = `warning${classificationString}`;

        // Prioritry order lookup
        if (warning in BfMapMarkerType) {
            return warning as BfMapMarkerType;
        } else if (warningNoSuffix in BfMapMarkerType) {
            return warningNoSuffix as BfMapMarkerType;
        } else if (advisory in BfMapMarkerType) {
            return advisory as BfMapMarkerType;
        } else if (incident in BfMapMarkerType) {
            return incident as BfMapMarkerType;
        }

        // Log unable to find types
        if (DEBUG_MODE) {
            console.log(
                "AnyIconForClassification could not find icon for classification",
                classificationString
            );
        }

        return BfMapMarkerType.incidentOther;
    }

    static AccentColour(event: BfEventSimple): string | undefined {
        if (!event) return undefined;

        if (event.eventClass == BfEventClass.warning) {
            switch (event.alertLevel) {
                case BfAlertLevel.emergencyWarning:
                case BfAlertLevel.evacuateImmediately:
                    return "rgba(214,0,28,1)";
                case BfAlertLevel.watchAndAct:
                    return "rgba(255,121,0,1)";
                case BfAlertLevel.advice:
                    return "rgba(254,224,50,1)";
                case BfAlertLevel.communityUpdate:
                    return "rgba(250,13,228,1)";
                default:
                    return "rgba(73,152,236,1)";
            }
        } else if (event.eventClass === BfEventClass.advisory) {
            return "rgba(73,152,236,1)";
        }

        return undefined;
    }

    static HotspotColour(frp: number): string {
        if (frp >= 500) {
            return "#4c0809";
        } else if (frp >= 100) {
            return "#8c0608";
        } else if (frp >= 50) {
            return "#d52612";
        } else if (frp >= 30) {
            return "#eb4a18";
        } else if (frp >= 20) {
            return "#ff7921";
        } else if (frp >= 10) {
            return "#ff9d26";
        } else if (frp >= 1) {
            return "#ffce41";
        } else {
            return "#4c0809";
        }
    }

    static titlecase(str: string): string {
        return str.replace(/(^|\s)\S/g, function (t) {
            return t.toUpperCase();
        });
    }

    static TemperatureUnit(unit: TemperatureUnits): string {
        return MAP_TEMPERATURE_UNITS[unit];
    }

    static SpeedUnit(unit: SpeedUnits): string {
        return MAP_SPEED_UNITS[unit];
    }

    static DistanceUnit(unit: DistanceUnits): string {
        return MAP_DISTANCE_UNITS[unit];
    }

    static AndroidSound(sound: BfNotificationSound | undefined): string {
        if (!sound) return "default";
        return ANDROID_SOUNDS[sound];
    }

    static Sound(sound: BfNotificationSound | undefined): string {
        if (!sound) return "Default";
        return FRIENDLY_SOUNDS[sound];
    }

    static PlanToGroup(plan: BfPlanTypes | BfPlanTypes[]): BfGroupName {
        if (Array.isArray(plan)) {
            // If multiple plans, return highest tier group
            if (plan.includes(BfPlanTypes.enterprise)) {
                return BfGroupName.enterprise;
            } else if (plan.includes(BfPlanTypes.business)) {
                return BfGroupName.business;
            } else if (plan.includes(BfPlanTypes.pro)) {
                return BfGroupName.pro;
            } else {
                return BfGroupName.user;
            }
        } else {
            return PLAN_TO_GROUP_TYPES[plan];
        }
    }

    static PublishingStatus(status: BfPublishingStatus): string {
        return PUBLISHING_STATES[status];
    }

    static PublishingStatusEmoji(status: BfPublishingStatus): string {
        return PUBLISHING_STATES_EMOJI[status];
    }

    static ReportReason(reason: BfMediaReportReason): string {
        return MEDIA_REPORT_REASON[reason];
    }

    static MediaLicence(licence: BfMediaLicence): string {
        return MEDIA_LICENCES[licence];
    }

    // -45 should return "North-West"
    // 0 should return "North"
    // 45 should return "North-East"
    static DegreesToCardinal(degrees: number): string {
        const directions = [
            "North",
            "North-East",
            "East",
            "South-East",
            "South",
            "South-West",
            "West",
            "North-West",
        ];
        degrees = Friendly.DegreesToDegrees(degrees);
        const index = Math.round(((degrees % 360) / 360) * directions.length) % directions.length;
        return directions[index];
    }

    static DegreesToDegrees(degrees: number): number {
        // Fix from mapbox degrees
        degrees = (degrees + 90) % 360;

        if (degrees < 0) {
            degrees += 360;
        }
        return degrees;
    }
    static EventClassificationGroup(group: EventClassificationGroup): string {
        return MAP_EVENT_CLASSIFICATION_GROUPS[group];
    }
}
